import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  useHsCodesQuery,
  useCreateHsCodeMutation,
  useUpdateHsCodeMutation,
  useDeleteHsCodeMutation,
} from "../../Services";
import { HsCode } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface HsCodeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newHsCode?: HsCode) => void;
}

export const HsCodeModal: React.FC<HsCodeModalProps> = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: hsCodes = [], isLoading } = useHsCodesQuery({
    enabled: open,
  });

  const createMutation = useCreateHsCodeMutation();
  const updateMutation = useUpdateHsCodeMutation();
  const deleteMutation = useDeleteHsCodeMutation();

  const handleStartEdit = (record: HsCode) => {
    setEditingId(record.id);
    form.setFieldsValue({
      code: record.code,
      description: record.description || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        updateMutation.mutate(
          {
            id: editingId,
            code: values.code.trim(),
            description: values.description ? values.description.trim() : "",
          },
          {
            onSuccess: (res: any) => {
              if (res?.success) {
                message.success("HS Code updated successfully!");
                handleCancelEdit();
              } else {
                message.error(res?.message || "Failed to update HS Code.");
              }
            },
            onError: () => message.error("Something went wrong."),
          },
        );
      } else {
        createMutation.mutate(values, {
          onSuccess: (res: any) => {
            if (res?.success) {
              message.success("HS Code added successfully!");
              form.resetFields();
              if (onSuccess) {
                onSuccess(res?.data);
              }
            } else {
              message.error(res?.message || "Failed to add HS Code.");
            }
          },
          onError: () => message.error("Something went wrong."),
        });
      }
    } catch {
      message.error("Please enter a valid HS code.");
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res: any) => {
        if (res?.success) {
          message.success("HS Code deleted successfully!");
          if (editingId === id) {
            handleCancelEdit();
          }
        } else {
          message.error(res?.message || "Failed to delete HS Code.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const handleModalClose = () => {
    handleCancelEdit();
    onClose();
  };

  const columns = [
    {
      title: "HS Code",
      dataIndex: "code",
      key: "code",
      width: 160,
      render: (text: string, record: HsCode) => (
        <span
          className={`font-mono font-semibold ${
            record.id === editingId ? "text-blue-700 font-bold" : "text-blue-700"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => (
        <span className="text-gray-600 text-xs">{text || "-"}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center" as const,
      render: (_: any, record: HsCode) => (
        <Space size="small">
          <Button
            type="text"
            icon={
              <EditOutlined
                className={record.id === editingId ? "text-blue-600" : ""}
              />
            }
            size="small"
            onClick={() => handleStartEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this HS Code?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Customs HS Codes")}
      open={open}
      onCancel={handleModalClose}
      footer={modalFooter({
        actions: [{ label: "Close", onClick: handleModalClose }],
      })}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        className="my-3 bg-slate-50 border border-slate-200 p-3 rounded-lg"
      >
        <div className="grid grid-cols-2 gap-3">
          <Form.Item
            name="code"
            label="HS Code"
            rules={[{ required: true, message: "HS Code is required" }]}
            className="mb-0"
          >
            <Input
              placeholder="e.g. 7210.70.10"
              prefix={<PlusOutlined />}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description (Optional)"
            className="mb-0"
          >
            <Input placeholder="e.g. Flat-rolled iron/steel coil" />
          </Form.Item>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <Button size="middle" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            size="middle"
            onClick={handleFormSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            className={
              editingId
                ? "bg-blue-600 hover:bg-blue-700 font-semibold"
                : "bg-emerald-600 hover:bg-emerald-700 font-semibold"
            }
          >
            {editingId ? "Update HS Code" : "Add HS Code"}
          </Button>
        </div>
      </Form>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={hsCodes}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </Modal>
  );
};

